import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AppRoot } from "./app/AppRoot";
import { loadConfig } from "./config";
import { I18nProvider, resolveInitialLocale } from "./i18n/localization";
import { en } from "./i18n/en";
import { he } from "./i18n/he";
import { readSessionToken } from "./services/authToken";
import { capturesOfficerTokenAt } from "./features/public/publicFlow";
import "./styles.css";

// Bootstrap (docs/design/modules/04-web-client.md §3). Host-mode detection happens
// inside AppRoot; main.tsx only loads the runtime config and mounts React.

// Reset the host document for a full-viewport app even when the served page did NOT link
// the extracted global CSS — the app-service launch page loads only the JS bundle, so the
// styles.css reset is absent and the UA's default body margin (~16px) makes the document
// scroll. Applied to <html> and <body> (which inline element styles cannot reach). Mirrors
// the styles.css reset, so it is a no-op when that CSS is present.
function resetHostDocument(): void {
  for (const el of [document.documentElement, document.body]) {
    el.style.margin = "0";
    el.style.height = "100%";
    el.style.overflow = "hidden";
  }
}

// Runtime config locale, once loaded — used by the startup-error message below.
let runtimeLocale: string | undefined;

async function start() {
  resetHostDocument();
  // Persist a launch token from the entry URL (?token=) into sessionStorage once, at boot,
  // before any routing happens. In-SPA navigation uses plain <Link>s that drop the query
  // string, so without this a valid token would be lost when moving to /admin, /schedule,
  // etc. Capturing it here keeps the token out of subsequent URLs/history (sessionStorage is
  // the carrier) while ensuring authorized navigation never silently loses the session.
  // Never on the public surface ("/", "/join") or a retired entry path: a legacy ?token= there
  // is dropped by the router and must not be stored or reused.
  if (capturesOfficerTokenAt(window.location.pathname)) readSessionToken(new URLSearchParams(window.location.search));
  runtimeLocale = (await loadConfig()).locale;
  const root = ReactDOM.createRoot(document.getElementById("root")!);
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <I18nProvider>
          <AppRoot />
        </I18nProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}

start().catch((err) => {
  // Same explicit-choice rules as the app (stored toggle → runtime config → English); never
  // inferred from the browser language.
  document.body.innerText = (resolveInitialLocale(runtimeLocale) === "he" ? he : en).app.startupError;
  // eslint-disable-next-line no-console
  console.error(err);
});
